import React, { useEffect, useState, useRef } from "react";
import { ICellEditorParams } from "ag-grid-community";
import { Select, Spin } from "antd";
import { TAG_COLORS } from "@/ts/constants";
import { GlobalTagRecordType, KwdColumns } from "@/ts/common-types";
import { KeywordMappingConfig, KeywordMappingOperation } from "@/ts/common-types";
import { addKeywordMappings } from "@/ts/utils";
import { showToast } from "@/ts/modals";

interface KeywordTagCellEditorParams extends ICellEditorParams {
    avlTagsData: {
        tagId: string;
        tagName: string;
    }[];
}

const KeywordTagCellEditor: React.FC<KeywordTagCellEditorParams> = (params) => {
    const { avlTagsData } = params;
    const tags: string[] = params.value || [];
    
    const initialTagIds = tags.map(tagName => {
        const tag = avlTagsData.find(t => t.tagName === tagName);
        return tag?.tagId || '';
    }).filter(id => id !== '');

    const [selectedValues, setSelectedValues] = useState<string[]>(initialTagIds);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    let colorIdx = 0;
    const field = params.colDef.field;
    
    if (field === KwdColumns.CATEGORY) colorIdx = 0;
    else if (field === KwdColumns.SUBCATEGORY) colorIdx = 1;
    else if (field === KwdColumns.BRAND) colorIdx = 2;
    
    const colors = TAG_COLORS[colorIdx];

    // Sort options to put selected ones at top
    const options = [
        // Selected options first with color coding
        ...avlTagsData
            .filter(tag => selectedValues.includes(tag.tagId))
            .map(tag => ({
                value: tag.tagId,
                label: tag.tagName,
                className: 'selected-option',
                style: {
                    backgroundColor: colors.background,
                    color: colors.fill,
                    fontWeight: 500
                }
            })),

        ...avlTagsData
            .filter(tag => !selectedValues.includes(tag.tagId))
            .map(tag => ({
                value: tag.tagId,
                label: tag.tagName
            }))
    ];

    useEffect(() => {
        setOpen(true);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node) &&
                !(event.target as HTMLElement).closest(".ant-select-dropdown")
            ) {
                setSelectedValues(initialTagIds);
                params.api.stopEditing();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [params.api, initialTagIds]);

    const handleChange = async (newValue: string | string[]) => {
        const newValues = Array.isArray(newValue) ? newValue : [];
        
        let finalValues: string[];
        let operation: KeywordMappingOperation;
        let changedTagIds: number[];

        const added = newValues.filter(v => !selectedValues.includes(v));
        const removed = selectedValues.filter(v => !newValues.includes(v));
        
        if (removed.length > 0) {
            finalValues = field === 'brand_list' ? [] : selectedValues.filter(v => !removed.includes(v));
            operation = KeywordMappingOperation.REMOVE;
            changedTagIds = removed.map(v => parseInt(v));
        } else {
            finalValues = field === 'brand_list' ? [added[0]] : [...selectedValues, ...added];
            operation = KeywordMappingOperation.UPSERT;
            changedTagIds = added.map(v => parseInt(v));
        }

        if (JSON.stringify(selectedValues) !== JSON.stringify(finalValues)) {
            setLoading(true);
            
            const updateConfig: KeywordMappingConfig = {
                keywords: [params.data.sys_keyword_id],
                operation
            };

            // Only include the changed tags in the payload
            if (field === KwdColumns.CATEGORY) {
                updateConfig.category_list = changedTagIds;
            } else if (field === KwdColumns.SUBCATEGORY) {
                updateConfig.subcategory_list = changedTagIds;
            } else if (field === 'brand_list') {
                updateConfig.brand_list = changedTagIds;
            }

            try {
                const success = await addKeywordMappings(updateConfig);

                if (success) {
                    // Convert all final tag IDs to the proper data structure
                    const updatedTags = finalValues.map(id => ({
                        id: parseInt(id),
                        name: avlTagsData.find(tag => tag.tagId === id)?.tagName || ''
                    })).filter(tag => tag.name !== '');

                    params.node.setDataValue(field, updatedTags);
                    
                    setSelectedValues(finalValues);

                    const fieldName = field.replace('_list', '');
                    if (operation === 'remove') {
                        showToast(
                            "success",
                            `Successfully removed ${fieldName} tag${changedTagIds.length > 1 ? 's' : ''}`
                        );
                    } else {
                        showToast(
                            "success",
                            `Successfully added ${fieldName} tag${changedTagIds.length > 1 ? 's' : ''}`
                        );
                    }
                }
            } catch (error) {
                console.error('Error updating keyword mappings:', error);
            } finally {
                setLoading(false);
                params.api.stopEditing();
            }
        } else {
            params.api.stopEditing();
        }
    };

    return (
        <div
            ref={wrapperRef}
            style={{ width: "100%", height: "100%", padding: "4px" }}
        >
            <Spin spinning={loading}>
                <Select
                    mode="multiple"
                    value={selectedValues}
                    onChange={handleChange}
                    options={options}
                    style={{ width: "100%" }}
                    data-field={field}
                    showSearch
                    allowClear
                    autoFocus
                    open={open}
                    onDropdownVisibleChange={setOpen}
                    variant="borderless"
                    placeholder={`Select ${field.replace('_list', '')}`}
                    filterOption={(input, option) =>
                        (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                    }
                    listHeight={300}
                    menuItemSelectedIcon={null}
                    dropdownStyle={{
                        padding: "4px"
                    }}
                    className="clean-select-editor"
                />
            </Spin>
        </div>
    );
};

export default KeywordTagCellEditor;